import React from 'react';
import moment from 'moment';
import 'moment/locale/id';
import AppointmentDialog from './AppointmentDialog';
import HospitalSelector from './shared/HospitalSelector';
import DoctorSelector from './shared/DoctorSelector';
import { searchDoctors } from './ArticleFormNew/api';
import { statusColor, statusTint, SUMMARY_STATUSES, STATUS_LABEL_KEYS } from './shared/appointmentStatus';

const SOURCE_ICONS = {
  web: 'zmdi-globe',
  android: 'zmdi-smartphone-android',
  ios: 'zmdi-apple',
};

// The day feed pages by 100, so a busy day is still a long list. Grouping by
// the hour is what makes that scannable -- it is the modal's only structure.
// appointment_time is a free-form string column, so "7:15" turns up next to "07:15".
const hourOf = (timeLabel, fallback) => {
  const match = /^(\d{1,2}):\d{2}/.exec(timeLabel || '');
  return match ? `${match[1].padStart(2, '0')}:00` : fallback;
};

export default class AppointmentDayModal extends React.Component {
  constructor(props) {
    super(props);
    moment.locale(props.locale === 'id' ? 'id' : 'en');
    this.state = {
      loading: true,
      loadingMore: false,
      error: false,
      data: null,
      page: 1,
      status: '',
      q: '',
      searchText: '',
      hospitalId: props.hospitalId || '',
      doctor: props.doctor || null,
      selectedAppointment: null,
      animateIn: false,
    };
    // Guards page appends against responses from a superseded filter.
    this.requestSeq = 0;
  }

  componentDidMount() {
    this.load();
    // Same fade-in AnimatedModal uses: paint once without .show, then add it so
    // Bootstrap's .modal.fade transition has two states to move between.
    requestAnimationFrame(() => {
      if (!this.unmounted) this.setState({ animateIn: true });
    });
    document.addEventListener('keydown', this.handleEscape);
    // Bootstrap's own class, so the page behind the modal stops scrolling.
    document.body.classList.add('modal-open');
  }

  componentDidUpdate(prevProps, prevState) {
    const changed = ['status', 'q', 'hospitalId', 'doctor'].some(key => prevState[key] !== this.state[key]);
    if (changed) this.load();
  }

  componentWillUnmount() {
    this.unmounted = true;
    clearTimeout(this.searchTimer);
    clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.handleEscape);
    document.body.classList.remove('modal-open');
  }

  // Escape, the backdrop and the X all take this path, so every dismissal fades.
  handleClose = () => {
    this.setState({ animateIn: false });
    this.closeTimer = setTimeout(this.props.onClose, 200);
  };

  handleEscape = (event) => {
    if (event.key === 'Escape') this.handleClose();
  };

  query = () => {
    const { status, q, hospitalId, doctor } = this.state;
    const params = new URLSearchParams({ date: this.props.date });
    if (hospitalId) params.set('hospital_id', hospitalId);
    if (doctor) params.set('doctor_id', doctor.id);
    if (q) params.set('q', q);
    if (status !== '') params.set('status', status);
    return params;
  };

  load = (page = 1) => {
    const seq = ++this.requestSeq;
    // Page 1 repaints the list; later pages keep the rows on screen and only
    // disable the load-more button while the append is in flight.
    if (page === 1) this.setState({ loading: true, error: false, page });
    else this.setState({ loadingMore: true, page });

    const params = this.query();
    params.set('per_page', 50);
    params.set('page', page);

    fetch(`/api/appointments/day?${params}`)
      .then(response => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then(data => {
        if (this.unmounted || seq !== this.requestSeq) return;
        this.setState(prevState => {
          // Appends keep the latest aggregates but stack the new rows onto the
          // ones already on screen. Rows shifting between fetches (a live insert,
          // or the overflow clamp re-serving the last page) would repeat ids, so
          // already-loaded rows are dropped from the incoming page.
          let next = data;
          if (page > 1 && prevState.data) {
            const seen = new Set(prevState.data.appointments.data.map(row => row.id));
            const fresh = data.appointments.data.filter(row => !seen.has(row.id));
            next = { ...data, appointments: { ...data.appointments, data: prevState.data.appointments.data.concat(fresh) } };
          }
          return { data: next, loading: false, loadingMore: false };
        });
      })
      .catch(() => {
        if (this.unmounted || seq !== this.requestSeq) return;
        // A failed append leaves the loaded rows alone; only page 1 blanks
        // into the error message.
        this.setState({ loading: false, loadingMore: false, error: page === 1 });
      });
  };

  handleSearch = (event) => {
    const searchText = event.target.value;
    this.setState({ searchText });
    clearTimeout(this.searchTimer);
    // One request per pause in typing, not one per keystroke.
    this.searchTimer = setTimeout(() => this.setState({ q: searchText }), 300);
  };

  // The counters describe the whole day and the endpoint keeps them stable while the
  // list filters, so they are read straight off the payload.
  countFor = (status) => {
    const counts = (this.state.data && this.state.data.status_counts) || {};
    return counts[String(status)] || 0;
  };

  rows = () => ((this.state.data && this.state.data.appointments && this.state.data.appointments.data) || []);

  // The server orders by a string column, so "7:15" lands after "10:00" and a running
  // group would emit the same hour twice. Bucket first, then sort the hours.
  groupedRows = () => {
    const groups = new Map();
    this.rows().forEach(row => {
      const hour = hourOf(row.attributes.time_label, this.props.labels.no_time);
      if (!groups.has(hour)) groups.set(hour, []);
      groups.get(hour).push(row);
    });

    const isHour = hour => /^\d{2}:00$/.test(hour);
    return Array.from(groups, ([hour, rows]) => ({ hour, rows })).sort((a, b) => {
      // Appointments with no usable time go last rather than sorting by their label.
      if (isHour(a.hour) !== isHour(b.hour)) return isHour(a.hour) ? -1 : 1;
      return a.hour.localeCompare(b.hour);
    });
  };

  openTable = () => {
    const params = this.query();
    params.delete('status');
    params.set('view', 'table');
    window.location.href = `${window.location.pathname}?${params}`;
  };

  renderCounters() {
    const { labels } = this.props;

    return (
      <div className="appointment-day-modal__counters">
        {SUMMARY_STATUSES.map(status => (
          <div
            className="appointment-day-modal__counter"
            key={status}
            style={{ backgroundColor: statusTint(status), color: statusColor(status) }}
          >
            <span className="appointment-day-modal__counter-value">{this.countFor(status)}</span>
            <span className="appointment-day-modal__counter-label">{labels[STATUS_LABEL_KEYS[status]]}</span>
          </div>
        ))}
      </div>
    );
  }

  renderFilters() {
    const { labels, hospitals } = this.props;
    const { status, searchText, hospitalId, doctor } = this.state;

    return (
      <div className="appointment-day-modal__filters">
        <div className="appointment-day-modal__chips" role="group" aria-label={labels.status}>
          {[''].concat(SUMMARY_STATUSES).map(value => (
            <button
              key={String(value)}
              type="button"
              className={`appointment-day-modal__chip${status === value ? ' is-active' : ''}`}
              aria-pressed={status === value}
              onClick={() => this.setState({ status: value })}
            >
              {value === '' ? labels.all : labels[STATUS_LABEL_KEYS[value]]}
            </button>
          ))}
        </div>

        {/* Second row. The canvas fits these beside the chips because it only draws
            three of them; with seven, the search and both pickers get squeezed. */}
        <div className="appointment-day-modal__controls">
        <div className="appointment-day-modal__search form-group">
          <i className="zmdi zmdi-search appointment-day-modal__search-icon" aria-hidden="true" />
          <input
            type="text"
            className="form-control"
            placeholder={labels.day_search_placeholder}
            value={searchText}
            onChange={this.handleSearch}
          />
          <i className="form-group__bar" />
        </div>

        {!this.props.lockedHospital && (
          <div className="appointment-day-modal__select">
            <HospitalSelector
              hospitals={hospitals}
              selectedId={hospitalId}
              onSelect={id => this.setState({ hospitalId: id })}
              onClear={() => this.setState({ hospitalId: '' })}
              translations={{ ...labels, select_hospital: labels.all_hospitals_short }}
            />
          </div>
        )}

        <div className="appointment-day-modal__select">
          <DoctorSelector
            variant="dropdown"
            multiple={false}
            selectedDoctors={doctor ? [doctor] : []}
            onSelect={selected => this.setState({ doctor: selected })}
            onRemove={() => this.setState({ doctor: null })}
            searchDoctors={searchDoctors}
            translations={labels}
          />
        </div>
        </div>
      </div>
    );
  }

  renderRow(row) {
    const { labels } = this.props;
    const { attributes } = row;
    const color = statusColor(attributes.status);
    const label = labels[STATUS_LABEL_KEYS[attributes.status]] || labels.status;

    return (
      <div className="appointment-day-modal__row" key={row.id} role="row">
        <div className="appointment-day-modal__time" role="cell">{attributes.time_label || '--:--'}</div>

        <div className="appointment-day-modal__patient" role="cell">
          <span className="appointment-day-modal__avatar" aria-hidden="true">
            <i className={`zmdi ${SOURCE_ICONS[attributes.source] || 'zmdi-account'}`} />
          </span>
          <span className="appointment-day-modal__patient-text">
            <a className="appointment-day-modal__patient-name" href={attributes.url}>{attributes.name}</a>
            <small className="appointment-day-modal__mrn">
              {attributes.medical_record_number
                ? `${labels.mrn} ${attributes.medical_record_number}`
                : labels.no_mrn}
            </small>
          </span>
        </div>

        <div className="appointment-day-modal__doctor" role="cell">
          <span className="appointment-day-modal__doctor-name">{attributes.doctor_name}</span>
          <small className="appointment-day-modal__speciality">{attributes.doctor_speciality_name}</small>
        </div>

        <div className="appointment-day-modal__hospital" role="cell">{attributes.hospital_name}</div>

        <div role="cell">
          <span
            className="appointment-day-modal__status"
            style={{ color, backgroundColor: statusTint(attributes.status) }}
          >
            <span className="appointment-day-modal__status-dot" style={{ backgroundColor: color }} />
            {label}
          </span>
        </div>

        <div role="cell">
          <button
            type="button"
            className="appointment-day-modal__action"
            title={labels.view_detail}
            aria-label={labels.view_detail}
            onClick={() => this.setState({ selectedAppointment: row })}
          >
            <i className="zmdi zmdi-more-vert" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  renderBody() {
    const { labels } = this.props;
    const { loading, error } = this.state;

    if (loading) return <p className="appointment-day-modal__message">{labels.loading}</p>;
    if (error) return <p className="appointment-day-modal__message text-danger">{labels.load_error}</p>;

    const groups = this.groupedRows();
    if (groups.length === 0) return <p className="appointment-day-modal__message">{labels.no_appointments_day}</p>;

    return groups.map(group => (
      <div className="appointment-day-modal__group" key={group.hour}>
        <div className="appointment-day-modal__group-head">
          <span className="appointment-day-modal__group-hour">{group.hour}</span>
          <span className="appointment-day-modal__group-count">
            {labels.appointments_count.replace('%{count}', group.rows.length)}
          </span>
        </div>
        {group.rows.map(row => this.renderRow(row))}
      </div>
    ));
  }

  render() {
    const { labels, date } = this.props;
    const { data, selectedAppointment, animateIn, loading, loadingMore } = this.state;
    const total = (data && data.total) || 0;
    const pagination = data && data.pagination;
    // pagination.count is the filtered total; total stays the whole day.
    const shownOf = pagination ? pagination.count : total;
    const heading = moment(date).format('dddd, D MMMM YYYY');
    const summary = [
      labels.appointments_count.replace('%{count}', total),
      labels.hospitals_count.replace('%{count}', (data && data.hospitals_count) || 0),
      labels.doctors_count.replace('%{count}', (data && data.doctors_count) || 0),
    ].join(' · ');

    return (
      <React.Fragment>
        <div
          className={`modal fade appointment-day-modal${animateIn ? ' show' : ''}`}
          role="dialog"
          aria-modal="true"
          aria-label={heading}
          onClick={event => { if (event.target === event.currentTarget) this.handleClose(); }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header appointment-day-modal__header">
                <div className="appointment-day-modal__head-row">
                  <div>
                    <h5 className="modal-title appointment-day-modal__title">{heading}</h5>
                    <p className="appointment-day-modal__summary">{summary}</p>
                  </div>
                  <button type="button" className="close" aria-label={labels.close} onClick={this.handleClose}>
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                {this.renderCounters()}
              </div>

              <div className="appointment-day-modal__panel">
                {this.renderFilters()}
                <div className="appointment-day-modal__columns" role="row">
                  <span role="columnheader">{labels.col_time}</span>
                  <span role="columnheader">{labels.col_patient}</span>
                  <span role="columnheader">{labels.col_doctor}</span>
                  <span role="columnheader">{labels.col_hospital}</span>
                  <span role="columnheader">{labels.col_status}</span>
                  <span />
                </div>
              </div>

              {/* Only this scrolls: the counters, filters and footer stay put while a
                  200-row day is paged through. */}
              <div className="modal-body appointment-day-modal__body" role="table">
                {this.renderBody()}
              </div>

              <div className="modal-footer appointment-day-modal__footer">
                <span className="appointment-day-modal__showing">
                  {labels.showing_of
                    .replace('%{shown}', this.rows().length)
                    .replace('%{total}', shownOf)}
                </span>
                {pagination && this.rows().length < pagination.count && (
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    disabled={loading || loadingMore}
                    onClick={() => this.load(this.state.page + 1)}
                  >
                    {labels.load_more}
                  </button>
                )}
                <button type="button" className="btn btn-outline-primary btn-sm" onClick={this.openTable}>
                  <i className="zmdi zmdi-open-in-new" aria-hidden="true" /> {labels.open_in_table}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className={`modal-backdrop fade appointment-day-modal__backdrop${animateIn ? ' show' : ''}`} />

        <AppointmentDialog
          open={Boolean(selectedAppointment)}
          onClose={() => this.setState({ selectedAppointment: null })}
          appointment={selectedAppointment}
        />
      </React.Fragment>
    );
  }
}
